import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';

import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile.ts';
import { PeopleLink } from '@confluence/people-directory';

import { ProfileCardWrapper, type TriggerPosition } from './ProfileCardWrapper.tsx';
import { useNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useNavigateToTeamsApp.ts';

export type ProfileLinkProps = {
	userId?: string | null;
	fullName: string;
	isAnonymous?: boolean;
	onUserLinkClick?: () => void;
	referralSource?: string;
	className?: string;
	style?: object;
	behavior?: 'card' | 'link';
	position?: TriggerPosition;
	offset?: [number, number];
	ariaLabel?: string;
	isUnderlinedByDefault?: boolean;
};

const linkStyles = xcss({
	textDecoration: 'underline',
	display: 'inline-block',
	cursor: 'pointer',
});

const i18n = defineMessages({
	profileLinkName: {
		id: 'profile.link.name',
		defaultMessage: '{fullName} profile',
		description: 'Text for non-anonymous user profile link',
	},
});

export const ProfileLink: FC<ProfileLinkProps> = ({
	userId,
	fullName,
	isAnonymous = false,
	onUserLinkClick,
	className,
	style,
	behavior = 'link',
	position = 'bottom-start',
	offset,
	ariaLabel,
	isUnderlinedByDefault = true,
}) => {
	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();
	const intl = useIntl();
	const { href: teamsAppPersonProfileUrl } = useNavigateToTeamsApp({
		type: 'USER',
		payload: {
			userId: userId ?? '',
		},
	});

	const renderNameComponent = () => {
		if (behavior === 'card') {
			return (
				<ProfileCardWrapper
					isAnonymous={isAnonymous}
					userId={userId}
					position={position}
					offset={offset}
					ariaLabel={ariaLabel}
				>
					<Box
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={style}
						xcss={linkStyles}
					>
						{fullName}
					</Box>
				</ProfileCardWrapper>
			);
		} else {
			const linkStyle = {
				...style,
				...(isUnderlinedByDefault ? { textDecoration: 'underline' } : {}),
			};

			return (
				<PeopleLink
					target="_blank"
					onClick={onUserLinkClick}
					href={teamsAppPersonProfileUrl}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={className}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={linkStyle}
					data-testid="profile-link"
					aria-label={ariaLabel || intl.formatMessage(i18n.profileLinkName, { fullName })}
				>
					{fullName}
				</PeopleLink>
			);
		}
	};

	return (
		<>
			{isAnonymous || !userId || userId === '' ? (
				<FormattedMessage
					id="profile.anonymous.name"
					defaultMessage="Anonymous"
					description="Text for Anonymous user profile link."
				/>
			) : (
				<>{hasViewUserProfilePermission ? renderNameComponent() : <span>{fullName}</span>}</>
			)}
		</>
	);
};
